import { config } from '../config';
import { LiveReconnectScheduler } from './liveReconnect';
import { HTTP_UNAUTHORIZED } from '../constants/botApi';
import { validateAndRefreshToken } from '../utils/tokenUtils';

/**
 * Calendar WebSocket Service
 *
 * Connects to calendar_sync_service.py (port 6335) to receive:
 * - Bot active notifications (when bot joins a meeting)
 * - Bot status updates (creating, active, failed, terminated)
 * - Calendar sync status
 */

export interface CalendarBot {
  source: 'calendar' | 'manual';  // Source of the bot (calendar-based or manually added)
  botSessionId: string;
  botId: string;        // The room_id for WebSocket connections
  eventTitle: string;
  meetingUrl: string;
  startTime: string;
  status: 'scheduled' | 'creating' | 'joining' | 'active' | 'failed' | 'terminated';
  platform: 'meet' | 'zoom' | 'teams';
  websocketUrl?: string;  // URL to recall_websocket_service.py for receiving transcripts
  isSalespersonHost?: boolean;  // Whether salesperson is the meeting host (auto-detected from calendar)
  /** bot_terminated only: true when the bot ended without ever being let in. Older servers omit it. */
  notAdmitted?: boolean;
  /** bot_terminated only: why it was never let in (constants/botAdmission.ts), or null. */
  notAdmittedReason?: string | null;
}

export interface CalendarIntegration {
  id: string;
  provider: 'google';
  email: string;
  syncStatus: 'active' | 'paused' | 'error';
  lastSyncedAt?: string;
  lastError?: string;
}

interface CalendarWebSocketMessage {
  type: 'connection_established' | 'active_bots_list' | 'bot_scheduled' | 'bot_creating' |
        'bot_joining' | 'bot_active' | 'bot_failed' | 'bot_terminated' | 'sync_status' | 'error' | 'pong';
  data?: any;
  message?: string;
}

class CalendarWebSocketService {
  private websocket: WebSocket | null = null;
  private currentUrl: string | null = null;
  private baseUrl: string | null = null;  // Store base URL for reconnection
  // The page holds this socket for its whole life (bot_active/bot_terminated
  // arrive here), so it retries until disconnect(), session or not.
  private readonly reconnect = new LiveReconnectScheduler('calendar', () =>
    this.connect(this.baseUrl || undefined, this.firebaseUid || undefined)
  );
  private pingInterval: NodeJS.Timeout | null = null;
  private firebaseUid: string | null = null;
  private isConnected: boolean = false;
  private isConnecting: boolean = false;  // Lock to prevent concurrent connection attempts

  // Callbacks for handling calendar events
  private onActiveBotsCallback: ((bots: CalendarBot[]) => void) | null = null;
  private onBotActiveCallback: ((bot: CalendarBot) => void) | null = null;
  private onBotStatusChangeCallback: ((bot: CalendarBot) => void) | null = null;
  private onIntegrationStatusCallback: ((integration: CalendarIntegration) => void) | null = null;

  // Cache the last received bot list to handle race conditions
  private lastReceivedBots: CalendarBot[] | null = null;

  /**
   * Set the Firebase UID for this user (required for room-based updates)
   */
  setFirebaseUid(uid: string): void {
    this.firebaseUid = uid;
    console.log('📅 Calendar service set Firebase UID:', uid);
  }

  /**
   * Connect to the calendar sync service WebSocket with Firebase token authentication
   */
  async connect(url?: string, firebaseUid?: string): Promise<void> {
    this.reconnect.activate();

    // Guard: Prevent duplicate connections (check both WebSocket state AND connecting flag)
    if (this.isConnecting || (this.websocket && (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING))) {
      console.log('📅 Calendar WebSocket already connected or connecting, skipping duplicate connection');
      return;
    }

    // Set connecting flag immediately to prevent race conditions (React StrictMode double-invoke)
    this.isConnecting = true;

    // Clean up any existing closed/failed connection before creating new one
    if (this.websocket) {
      console.log('📅 Closing existing calendar WebSocket before reconnecting');
      this.websocket.close();
      this.websocket = null;
    }

    if (firebaseUid) {
      this.firebaseUid = firebaseUid;
    }

    // Always pull a fresh token at (re)connect time. Reading from Redux is
    // unsafe — Redux can lag Firebase's silent refresh and reconnects during
    // long sessions are exactly when a stale token would be rejected.
    const { token } = await validateAndRefreshToken();

    // disconnect() ran while the token was being fetched: open nothing.
    if (!this.reconnect.isActive()) return;

    if (!token) {
      this.isConnecting = false;
      console.error('📅 No Firebase token available for calendar service authentication');
      // No socket was opened, so no close event will retry: retry from here.
      this.reconnect.scheduleReconnect();
      throw new Error('Authentication token required');
    }

    return new Promise((resolve, reject) => {
      try {
        // Use provided URL or default to calendar service (websockets library, no /ws path needed)
        this.baseUrl = url || (config.server as any).calendarUrl || 'ws://localhost:6335';

        if (!this.baseUrl) {
          throw new Error('Calendar WebSocket base URL is not configured');
        }

        // Add Firebase token to URL for authentication (same as production_final_server.py)
        const separator = this.baseUrl.includes('?') ? '&' : '?';
        this.currentUrl = this.baseUrl + separator + 'token=' + encodeURIComponent(token);

        console.log('📅 Connecting to calendar sync service');

        if (!this.currentUrl) {
          throw new Error('Calendar WebSocket URL is not set');
        }

        const socket = new WebSocket(this.currentUrl);
        this.websocket = socket;

        socket.onopen = () => {
          if (this.websocket !== socket) return;
          this.isConnected = true;
          this.isConnecting = false;  // Clear connecting flag on success
          this.reconnect.markConnected();
          console.log('📅 Calendar WebSocket connected and authenticated');

          // No need to send identification - token authentication already handled by backend

          // Set up ping interval (every 30 seconds)
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
          }
          this.pingInterval = setInterval(() => {
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
              this.send({ type: 'ping' });
            }
          }, 30000);

          // Request active bots immediately on (re)connect to ensure we have latest state
          // This makes the system dynamic - no need to wait for next poll cycle
          setTimeout(() => {
            this.requestActiveBots();
          }, 100);

          resolve();
        };

        socket.onmessage = (event) => {
          try {
            const data: CalendarWebSocketMessage = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('❌ Error parsing calendar WebSocket message:', error);
          }
        };

        socket.onclose = (event) => {
          // A socket this service replaced or closed on purpose reports nothing.
          if (this.websocket !== socket) return;
          this.websocket = null;
          console.log('📅 Calendar WebSocket closed:', event.code, event.reason);
          this.isConnected = false;
          this.isConnecting = false;  // Clear connecting flag on close

          // Clear ping interval
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
          }

          // Any drop (including a server-side 1000 close, e.g. a deploy) is
          // retried with a fresh token; on open it re-requests the bot list.
          this.reconnect.scheduleReconnect();
        };

        socket.onerror = (error) => {
          console.error('📅 Calendar WebSocket error:', error);
          // Clear connecting flag on error (only the current socket owns it)
          if (this.websocket === socket) this.isConnecting = false;
          reject(new Error('Calendar WebSocket connection failed'));
        };

      } catch (error) {
        this.isConnecting = false;  // Clear connecting flag on exception
        reject(error);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: CalendarWebSocketMessage): void {

    switch (data.type) {
      case 'connection_established':
        break;

      case 'active_bots_list':
        const bots = data.data?.bots || [];

        // Cache the bot list for late-registering callbacks
        this.lastReceivedBots = bots as CalendarBot[];

        // Call the onActiveBots callback with list of bots
        if (this.onActiveBotsCallback) {
          this.onActiveBotsCallback(bots as CalendarBot[]);
        }
        break;

      case 'bot_scheduled':
        if (this.onBotStatusChangeCallback) {
          this.onBotStatusChangeCallback(data.data as CalendarBot);
        }
        break;

      case 'bot_creating':
        console.log('📅 Bot creating:', data.data);
        if (this.onBotStatusChangeCallback) {
          this.onBotStatusChangeCallback(data.data as CalendarBot);
        }
        break;

      case 'bot_joining':
        console.log('📅 Bot joining meeting:', data.data);
        const joiningBot = data.data as CalendarBot;

        // Update cached bot list
        if (this.lastReceivedBots) {
          const index = this.lastReceivedBots.findIndex(b => b.botSessionId === joiningBot.botSessionId);
          if (index >= 0) {
            this.lastReceivedBots[index] = joiningBot;
          } else {
            this.lastReceivedBots.push(joiningBot);
          }
        } else {
          this.lastReceivedBots = [joiningBot];
        }

        // Notify status change callback
        if (this.onBotStatusChangeCallback) {
          this.onBotStatusChangeCallback(joiningBot);
        }
        break;

      case 'bot_active':
        console.log('📅 Bot is now ACTIVE:', data.data);
        const bot = data.data as CalendarBot;

        // Update cached bot list
        if (this.lastReceivedBots) {
          const index = this.lastReceivedBots.findIndex(b => b.botSessionId === bot.botSessionId);
          if (index >= 0) {
            this.lastReceivedBots[index] = bot;
          } else {
            this.lastReceivedBots.push(bot);
          }
        } else {
          this.lastReceivedBots = [bot];
        }

        // CRITICAL: Call the onBotActive callback with botId (the room_id)
        if (this.onBotActiveCallback) {
          this.onBotActiveCallback(bot);
        }

        if (this.onBotStatusChangeCallback) {
          this.onBotStatusChangeCallback(bot);
        }
        break;

      case 'bot_failed':
        console.error('📅 Bot creation failed:', data.data);
        if (this.onBotStatusChangeCallback) {
          this.onBotStatusChangeCallback(data.data as CalendarBot);
        }
        break;

      case 'bot_terminated':
        console.log('📅 Bot terminated:', data.data);
        if (this.onBotStatusChangeCallback) {
          this.onBotStatusChangeCallback(data.data as CalendarBot);
        }
        break;

      case 'sync_status':
        console.log('📅 Calendar sync status:', data.data);
        if (this.onIntegrationStatusCallback) {
          this.onIntegrationStatusCallback(data.data as CalendarIntegration);
        }
        break;

      case 'error':
        console.error('📅 Calendar service error:', data.message);
        break;

      case 'pong':
        // Heartbeat response
        break;

      default:
        console.log('📅 Unknown calendar message type:', data.type);
    }
  }

  /**
   * Send initial identification message
   */
  private sendIdentification(): void {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('📅 Cannot send identification - WebSocket not connected');
      return;
    }

    const message = {
      type: 'identify',
      firebase_uid: this.firebaseUid
    };

    console.log('📅 Sending identification:', message);
    this.websocket.send(JSON.stringify(message));
  }

  /**
   * Send a message to the calendar service
   */
  private send(data: any): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(data));
    }
  }

  /**
   * Register callback for receiving active bots list on connect
   * If bot list was already received (before callback registration), replay it immediately
   */
  onActiveBots(callback: (bots: CalendarBot[]) => void): void {
    this.onActiveBotsCallback = callback;

    // Immediately call with cached data if available (handles race condition)
    if (this.lastReceivedBots !== null) {
      console.log('📅 Replaying cached bot list to newly registered callback:', this.lastReceivedBots);
      callback(this.lastReceivedBots);
    }
  }

  /**
   * Register callback for when a bot becomes active
   * CRITICAL: This is called with the botId which serves as room_id
   */
  onBotActive(callback: (bot: CalendarBot) => void): void {
    this.onBotActiveCallback = callback;
  }

  /**
   * Register callback for bot status changes
   */
  onBotStatusChange(callback: (bot: CalendarBot) => void): void {
    this.onBotStatusChangeCallback = callback;
  }

  /**
   * Register callback for integration status changes
   */
  onIntegrationStatus(callback: (integration: CalendarIntegration) => void): void {
    this.onIntegrationStatusCallback = callback;
  }

  /**
   * Request current active bots from the service
   */
  requestActiveBots(): void {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('📅 Cannot request active bots - not connected');
      return;
    }

    this.send({
      type: 'get_active_bots',
      firebase_uid: this.firebaseUid
    });
  }

  /**
   * Terminate an active bot (removes it from the meeting). The backend needs
   * the user's token and only finds the user's own bots. A failure carries
   * the HTTP status so the caller can tell a 401/403 from anything else.
   * `alreadyEnded`: the backend found the bot already out of its call.
   */
  async terminateBot(botSessionId: string): Promise<{ success: boolean; message?: string; error?: string; status?: number; alreadyEnded?: boolean }> {
    try {
      console.log('📅 Terminating bot:', botSessionId);

      // A fresh Firebase token, the same source as apiService and every
      // socket: Redux can lag Firebase's silent refresh.
      const { token } = await validateAndRefreshToken();

      if (!token) {
        return { success: false, error: 'Authentication token required', status: HTTP_UNAUTHORIZED };
      }

      // Call backend API to terminate the bot
      const response = await fetch(`${config.server.backendUrl}/api/calendar/bots/${encodeURIComponent(botSessionId)}/terminate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        return { success: false, error: result?.error || 'Failed to terminate bot', status: response.status };
      }

      console.log('📅 Bot terminated successfully:', botSessionId);

      // Optimistically update local cache - remove from active bots list
      if (this.lastReceivedBots) {
        this.lastReceivedBots = this.lastReceivedBots.map(bot => {
          if (bot.botSessionId === botSessionId) {
            return { ...bot, status: 'terminated' as const };
          }
          return bot;
        });

        // Notify callback of status change
        if (this.onBotStatusChangeCallback) {
          const terminatedBot = this.lastReceivedBots.find(b => b.botSessionId === botSessionId);
          if (terminatedBot) {
            this.onBotStatusChangeCallback(terminatedBot);
          }
        }

        // Also notify active bots callback to refresh the full list
        if (this.onActiveBotsCallback) {
          console.log('📅 Triggering active bots refresh after termination');
          this.onActiveBotsCallback(this.lastReceivedBots);
        }
      }

      return { success: true, message: result?.message, alreadyEnded: result?.alreadyEnded === true };
    } catch (error) {
      console.error('📅 Error terminating bot:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to terminate bot'
      };
    }
  }

  /**
   * Disconnect from the calendar service
   */
  disconnect(): void {
    this.reconnect.stop();

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.websocket) {
      this.websocket.close(1000, 'Client disconnect');
      this.websocket = null;
    }

    this.isConnected = false;
    this.isConnecting = false;  // Clear connecting flag on disconnect
  }

  /**
   * Check if the service is connected
   */
  isServiceConnected(): boolean {
    return this.isConnected;
  }
}

export const calendarWebSocketService = new CalendarWebSocketService();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).calendarWebSocketService = calendarWebSocketService;
}
